import { useEffect, useState } from "react";
import { Brain, Save, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const FIELDS = [
  {
    key: "buy_box",
    label: "Your buy box",
    hint: "Property types, price points, condition and anything you will not touch.",
    placeholder: "Single family 1950s or newer, $150k to $600k ARV, cosmetic to medium rehab. No mobile homes, no flood zone.",
    rows: 3,
  },
  {
    key: "markets",
    label: "Markets you work",
    hint: "Cities, counties or zips, and how hot each one is right now.",
    placeholder: "Denver metro and Colorado Springs. Both move fast, cash buyers pay up for anything turnkey.",
    rows: 2,
  },
  {
    key: "min_assignment_fee",
    label: "Minimum assignment fee",
    hint: "The least you will do a deal for, and when you flex on it.",
    placeholder: "$10k normally, $5k on anything under $200k ARV if it closes in two weeks.",
    rows: 2,
  },
  {
    key: "mao_guidance",
    label: "How you set max offers",
    hint: "This is the big one. Tell it when 65 percent makes sense and when 80 percent makes sense.",
    placeholder:
      "Heavy rehab or rural, 65 percent of ARV. Standard cosmetic flip in the metro, 70 to 75 percent. Hot neighborhood, light rehab or a novation exit, 80 to 85 percent is still a great deal. Judge the buyer's profit in dollars first, the percentage second.",
    rows: 5,
  },
  {
    key: "red_flags",
    label: "Automatic red flags",
    hint: "What makes you walk away no matter how good the spread looks.",
    placeholder: "Daisy chains, no assignable contract, seller still 'thinking about it', foundation work with no bid.",
    rows: 3,
  },
  {
    key: "training_notes",
    label: "Your teaching notes",
    hint: "Paste anything you teach: scripts, negotiation rules, how you underwrite, your own war stories.",
    placeholder: "Anything you want every AI answer to reflect.",
    rows: 8,
  },
];

export default function AdminAiCoach() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/ai-rulebook")
      .then(({ data }) => setForm(data))
      .catch(() => toast.error("Could not load the rulebook"));
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/ai-rulebook", {
        buy_box: form.buy_box || "",
        markets: form.markets || "",
        min_assignment_fee: form.min_assignment_fee || "",
        mao_guidance: form.mao_guidance || "",
        red_flags: form.red_flags || "",
        training_notes: form.training_notes || "",
      });
      setForm(data);
      toast.success("Saved. Every AI answer follows this from now on");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  if (!form) {
    return <div className="flex justify-center py-16"><span className="spinner" /></div>;
  }

  return (
    <div className="max-w-3xl" data-testid="admin-ai-coach">
      <div className="section-tag mb-3"><span className="dot" /> Admin command</div>
      <h1 className="h-display text-3xl sm:text-4xl">AI Rulebook</h1>
      <p className="text-sm mt-2 mb-6" style={{ color: "var(--text-muted)" }}>
        The AI Coach, the calculator gut check and the JV deal triage all read this before they answer.
        Whatever you write here overrides the generic rules of thumb they would use otherwise.
      </p>

      <div
        className="p-4 rounded-2xl mb-8 flex items-start gap-3"
        style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}
      >
        <Info size={16} style={{ color: "var(--lime)" }} className="mt-0.5 shrink-0" />
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
          Leave a field blank and the AI falls back to its own judgement for that part. The more you write in
          &quot;How you set max offers&quot;, the less likely it is to call a good deal bad.
        </p>
      </div>

      <div className="space-y-6">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="field-label">{f.label}</label>
            <p className="text-xs mb-2" style={{ color: "var(--text-dim)" }}>{f.hint}</p>
            <textarea
              className="input-base w-full"
              rows={f.rows}
              placeholder={f.placeholder}
              value={form[f.key] || ""}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              data-testid={`rulebook-${f.key}`}
            />
          </div>
        ))}
      </div>

      <button
        onClick={save}
        disabled={busy}
        className="btn-primary mt-8 flex items-center gap-2"
        data-testid="rulebook-save"
      >
        {busy ? <span className="spinner" /> : <Save size={15} />} Save rulebook
      </button>

      <div className="flex items-center gap-2 mt-6 text-xs" style={{ color: "var(--text-dim)" }}>
        <Brain size={13} /> Applies to the AI Coach chat, calculator gut checks, JV triage and AI offer analysis.
      </div>
    </div>
  );
}
